import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { DEFAULT_TOPICS } from '../data/defaultData';
import { loadTopics, saveTopics, loadSettings, saveSettings } from '../utils/storage';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [topics, setTopics] = useState(() => loadTopics(DEFAULT_TOPICS));
  const [settings, setSettings] = useState(() => loadSettings());

  useEffect(() => { saveTopics(topics); }, [topics]);
  useEffect(() => { saveSettings(settings); }, [settings]);

  const updateTopic = useCallback((topicId, updater) => {
    setTopics(prev => prev.map(t => t.id === topicId ? updater(t) : t));
  }, []);

  const reorderMantras = useCallback((topicId, fromIdx, toIdx) => {
    setTopics(prev => prev.map(t => {
      if (t.id !== topicId) return t;
      const m = [...t.mantras];
      const [moved] = m.splice(fromIdx, 1);
      m.splice(toIdx, 0, moved);
      return { ...t, mantras: m };
    }));
  }, []);

  const addMantra = useCallback((topicId, mantra) => {
    setTopics(prev => prev.map(t => t.id === topicId ? { ...t, mantras: [...t.mantras, mantra] } : t));
  }, []);

  const updateMantra = useCallback((topicId, mantraId, patch) => {
    setTopics(prev => prev.map(t => {
      if (t.id !== topicId) return t;
      return { ...t, mantras: t.mantras.map(m => m.id === mantraId ? { ...m, ...patch } : m) };
    }));
  }, []);

  const deleteMantra = useCallback((topicId, mantraId) => {
    setTopics(prev => prev.map(t => t.id === topicId ? { ...t, mantras: t.mantras.filter(m => m.id !== mantraId) } : t));
  }, []);

  const addSanskarsToYagya = useCallback((sanskarGroupIds) => {
    setTopics(prev => {
      const sanskarsTopic = prev.find(t => t.id === 'sanskars');
      if (!sanskarsTopic) return prev;
      // Gather all mantras belonging to selected sanskar groups (preserve original order)
      const allMantras = sanskarsTopic.mantras.filter(m => sanskarGroupIds.includes(m.sanskarGroup));
      if (allMantras.length === 0) return prev;
      // Mark each with sourceId so we can later remove
      const newOnes = allMantras.map(m => ({
        ...m,
        id: m.id + '-yagya-' + Date.now() + '-' + Math.random().toString(36).slice(2,6),
        sourceId: m.id,
        sanskarGroupSource: m.sanskarGroup,
      }));
      return prev.map(t => {
        if (t.id !== 'daily-yagya') return t;
        // Build new yagya mantra list by inserting each new sanskar mantra after its insertAfterYagyaId
        const yagyaCore = t.mantras.filter(m => !m.sourceId); // only original yagya mantras
        const yagyaSanskars = t.mantras.filter(m => m.sourceId); // existing sanskar inserts (keep)
        // Build map: yagyaMantraId -> [sanskar mantras to insert after it]
        const insertMap = {};
        // First, keep existing yagya-sanskars at their positions
        yagyaSanskars.forEach(m => {
          const key = m.insertAfterYagyaId || 'end';
          (insertMap[key] = insertMap[key] || []).push(m);
        });
        newOnes.forEach(m => {
          const key = m.insertAfterYagyaId || 'end';
          (insertMap[key] = insertMap[key] || []).push(m);
        });
        // Now build final list
        const result = [];
        yagyaCore.forEach(ym => {
          result.push(ym);
          if (insertMap[ym.id]) {
            result.push(...insertMap[ym.id]);
            delete insertMap[ym.id];
          }
        });
        // Append any with unmatched insertAfterYagyaId (or 'end')
        Object.values(insertMap).forEach(arr => result.push(...arr));
        return { ...t, mantras: result };
      });
    });
  }, []);

  const removeSanskarsFromYagya = useCallback((sourceIds) => {
    setTopics(prev => prev.map(t => {
      if (t.id !== 'daily-yagya') return t;
      return { ...t, mantras: t.mantras.filter(m => !sourceIds.includes(m.sourceId)) };
    }));
  }, []);

  const syncSanskarsInYagya = useCallback((selectedGroupIds) => {
    // Sync: adds new selected groups, removes deselected ones
    setTopics(prev => {
      const yagya = prev.find(t => t.id === 'daily-yagya');
      const sanskarsTopic = prev.find(t => t.id === 'sanskars');
      if (!yagya || !sanskarsTopic) return prev;
      const currentGroups = [...new Set(yagya.mantras.filter(m => m.sanskarGroupSource).map(m => m.sanskarGroupSource))];
      const toAdd = selectedGroupIds.filter(g => !currentGroups.includes(g));
      const toRemove = currentGroups.filter(g => !selectedGroupIds.includes(g));

      return prev.map(t => {
        if (t.id !== 'daily-yagya') return t;
        // Remove deselected groups
        let mantras = t.mantras.filter(m => !m.sanskarGroupSource || !toRemove.includes(m.sanskarGroupSource));
        // Add new groups: gather their mantras with new ids
        const newOnes = sanskarsTopic.mantras
          .filter(m => toAdd.includes(m.sanskarGroup))
          .map(m => ({
            ...m,
            id: m.id + '-yagya-' + Date.now() + '-' + Math.random().toString(36).slice(2,6),
            sourceId: m.id,
            sanskarGroupSource: m.sanskarGroup,
          }));
        if (newOnes.length === 0) return { ...t, mantras };
        // Re-build with insertion logic
        const yagyaCore = mantras.filter(m => !m.sourceId);
        const yagyaSanskars = mantras.filter(m => m.sourceId);
        const insertMap = {};
        yagyaSanskars.forEach(m => {
          const key = m.insertAfterYagyaId || 'end';
          (insertMap[key] = insertMap[key] || []).push(m);
        });
        newOnes.forEach(m => {
          const key = m.insertAfterYagyaId || 'end';
          (insertMap[key] = insertMap[key] || []).push(m);
        });
        const result = [];
        yagyaCore.forEach(ym => {
          result.push(ym);
          if (insertMap[ym.id]) {
            result.push(...insertMap[ym.id]);
            delete insertMap[ym.id];
          }
        });
        Object.values(insertMap).forEach(arr => result.push(...arr));
        return { ...t, mantras: result };
      });
    });
  }, []);

  const renameTopic = useCallback((topicId, newTitle) => {
    setTopics(prev => prev.map(t => t.id === topicId ? { ...t, title: newTitle } : t));
  }, []);

  const updateSanskarMantraInsert = useCallback((mantraId, insertAfterYagyaId) => {
    setTopics(prev => prev.map(t => {
      if (t.id !== 'sanskars') return t;
      return { ...t, mantras: t.mantras.map(m => m.id === mantraId ? { ...m, insertAfterYagyaId } : m) };
    }));
  }, []);

  const resetTopics = useCallback(() => {
    setTopics(DEFAULT_TOPICS);
  }, []);

  const updateSettings = useCallback((patch) => {
    setSettings(prev => ({ ...prev, ...patch }));
  }, []);

  return (
    <AppContext.Provider value={{
      topics, settings,
      updateTopic, reorderMantras, addMantra, updateMantra, deleteMantra,
      addSanskarsToYagya, removeSanskarsFromYagya, syncSanskarsInYagya,
      renameTopic, updateSanskarMantraInsert,
      resetTopics, updateSettings
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
